// Per-user storage: routes all localStorage.* calls through the signed-in user namespace.
import { scopedStorage as localStorage } from "../services/userStorage";
import React, { useMemo, useState } from 'react';
import { Check, CheckCircle2, Clock3, Edit3, Heart, Plus, Sparkles, Trash2, Users, X } from 'lucide-react';
import ScreenLayout from './ScreenLayout';

const STORAGE_KEY = 'prayer_app_requests';
const LEGACY_DEMO_REQUEST_IDS = new Set(['1', '2', '3', '4']);

const loadRequests = () => {
    try {
        const parsed = JSON.parse(localStorage.getItem(STORAGE_KEY));
        if (!Array.isArray(parsed)) return [];

        const requests = parsed.filter((request) => !LEGACY_DEMO_REQUEST_IDS.has(String(request?.id)));
        if (requests.length !== parsed.length) {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(requests));
        }
        return requests;
    } catch {
        return [];
    }
};

const PrayerRequests = () => {
    const [requests, setRequests] = useState(loadRequests);
    const [filter, setFilter] = useState('active');
    const [showForm, setShowForm] = useState(false);
    const [newRequest, setNewRequest] = useState({ name: '', request: '' });
    const [editingId, setEditingId] = useState(null);
    const [editText, setEditText] = useState('');

    const saveRequests = (nextRequests) => {
        setRequests(nextRequests);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(nextRequests));
    };

    const counts = useMemo(() => {
        const oneWeekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
        return {
            active: requests.filter((request) => !request.answered).length,
            answered: requests.filter((request) => request.answered).length,
            answeredThisWeek: requests.filter((request) => request.answered
                && request.answeredDate
                && new Date(request.answeredDate).getTime() > oneWeekAgo).length,
            all: requests.length
        };
    }, [requests]);

    const visibleRequests = requests.filter((request) => {
        if (filter === 'active') return !request.answered;
        if (filter === 'answered') return request.answered;
        return true;
    });

    const addRequest = () => {
        if (!newRequest.name.trim() || !newRequest.request.trim()) return;
        saveRequests([{
            id: Date.now(),
            name: newRequest.name.trim(),
            request: newRequest.request.trim(),
            date: new Date().toISOString().split('T')[0],
            answered: false
        }, ...requests]);
        setNewRequest({ name: '', request: '' });
        setShowForm(false);
        setFilter('active');
    };

    const toggleAnswered = (id) => {
        saveRequests(requests.map((request) => {
            if (request.id !== id) return request;
            const answered = !request.answered;
            return {
                ...request,
                answered,
                answeredDate: answered ? new Date().toISOString() : null
            };
        }));
    };

    const startEditing = (request) => {
        setEditingId(request.id);
        setEditText(request.request);
    };

    const saveEdit = (id) => {
        const text = editText.trim();
        if (!text) return;
        saveRequests(requests.map((request) => (
            request.id === id ? { ...request, request: text } : request
        )));
        setEditingId(null);
        setEditText('');
    };

    const removeRequest = (id) => {
        if (editingId === id) setEditingId(null);
        saveRequests(requests.filter((request) => request.id !== id));
    };

    const formatDate = (date) => new Intl.DateTimeFormat('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
    }).format(new Date(`${date}T12:00:00`));

    return (
        <ScreenLayout title="Prayer Requests" icon={Heart}>
            <section className="requests-hero">
                <div>
                    <p className="shared-kicker"><Sparkles size={14} /> A place to remember</p>
                    <h1>Hold their needs close.</h1>
                    <p>Keep a gentle record of the people and situations you want to bring before God.</p>
                </div>
                <button className="requests-add-button" onClick={() => setShowForm(true)}>
                    <Plus size={20} /> Add a prayer
                </button>
            </section>

            <div className="requests-summary" aria-label="Prayer request summary">
                <div><Clock3 size={19} /><span><strong>{counts.active}</strong>Active prayers</span></div>
                <div><CheckCircle2 size={19} /><span><strong>{counts.answered}</strong>Answered prayers</span></div>
                <div><Sparkles size={19} /><span><strong>{counts.answeredThisWeek}</strong>Answered this week</span></div>
                <div><Users size={19} /><span><strong>{counts.all}</strong>People & needs</span></div>
            </div>

            <nav className="requests-filters" aria-label="Filter prayer requests">
                {[
                    ['active', 'Active', counts.active],
                    ['answered', 'Answered', counts.answered],
                    ['all', 'All', counts.all]
                ].map(([id, label, count]) => (
                    <button key={id} className={filter === id ? 'is-active' : ''} onClick={() => setFilter(id)}>
                        {label}<span>{count}</span>
                    </button>
                ))}
            </nav>

            <section className="requests-list" aria-live="polite">
                {!visibleRequests.length ? (
                    <div className="requests-empty">
                        <Heart size={28} />
                        <h2>No prayers here yet</h2>
                        <p>Add a person or need whenever it is placed on your heart.</p>
                    </div>
                ) : visibleRequests.map((request) => (
                    <article className={`request-card ${request.answered ? 'is-answered' : ''}`} key={request.id}>
                        <button
                            className="request-status"
                            onClick={() => toggleAnswered(request.id)}
                            aria-label={request.answered ? 'Move prayer back to active' : 'Mark prayer as answered'}
                        >
                            {request.answered ? <CheckCircle2 size={22} /> : <Heart size={22} />}
                        </button>
                        <div>
                            <div className="request-card-heading">
                                <h2>{request.name}</h2>
                                <span>{request.answered ? 'Answered' : 'Praying'}</span>
                            </div>
                            {editingId === request.id ? (
                                <div className="request-edit">
                                    <textarea
                                        rows="4"
                                        autoFocus
                                        value={editText}
                                        onChange={(event) => setEditText(event.target.value)}
                                        aria-label={`Edit the prayer for ${request.name}`}
                                    />
                                    <button onClick={() => saveEdit(request.id)} disabled={!editText.trim()}>
                                        <Check size={16} /> Save
                                    </button>
                                    <button onClick={() => setEditingId(null)}><X size={16} /> Cancel</button>
                                </div>
                            ) : (
                                <p>{request.request}</p>
                            )}
                            <small>{request.answered ? 'Marked answered' : 'Added'} · {formatDate(request.date)}</small>
                        </div>
                        <div className="request-card-tools">
                            <button onClick={() => startEditing(request)} aria-label={`Edit the prayer for ${request.name}`}>
                                <Edit3 size={16} />
                            </button>
                            <button onClick={() => removeRequest(request.id)} aria-label={`Remove the prayer for ${request.name}`}>
                                <Trash2 size={16} />
                            </button>
                        </div>
                    </article>
                ))}
            </section>

            {showForm && (
                <div className="requests-overlay" onClick={() => setShowForm(false)}>
                    <section className="requests-form" onClick={(event) => event.stopPropagation()}>
                        <header>
                            <div><small>New prayer request</small><h2>Who is on your heart?</h2></div>
                            <button onClick={() => setShowForm(false)} aria-label="Close form"><X size={20} /></button>
                        </header>
                        <label>
                            Person or group
                            <input
                                autoFocus
                                value={newRequest.name}
                                onChange={(event) => setNewRequest({ ...newRequest, name: event.target.value })}
                                placeholder="A name, family, church, or group"
                            />
                        </label>
                        <label>
                            What would you like to pray for?
                            <textarea
                                rows="5"
                                value={newRequest.request}
                                onChange={(event) => setNewRequest({ ...newRequest, request: event.target.value })}
                                placeholder="Write the need in your own words..."
                            />
                        </label>
                        <button className="requests-save-button" onClick={addRequest} disabled={!newRequest.name.trim() || !newRequest.request.trim()}>
                            <Heart size={18} /> Keep this prayer
                        </button>
                    </section>
                </div>
            )}
        </ScreenLayout>
    );
};

export default PrayerRequests;
